import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  PROVIDER_LABELS,
  googleStartUrl,
  providerStartUrl,
  type ExternalProvider,
} from "../../api/auth";
import { API_URL } from "../../api/client";
import { meApi } from "../../api/me";
import type { UserOut } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatDateTime } from "../../shared/format";
import { Alert, Button, Card } from "../../ui";

const PROVIDERS: ExternalProvider[] = ["google", "facebook", "apple"];

export function IdentitiesPage() {
  const me = useOutletContext<UserOut>();
  const queryClient = useQueryClient();
  const identities = useQuery({
    queryKey: ["me", "identities"],
    queryFn: meApi.identities,
  });
  const [error, setError] = useState<string | null>(null);
  const unlink = useMutation({
    mutationFn: (provider: string) => meApi.unlinkIdentity(provider),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
    onError: (err) => setError(errorMessage(err)),
  });
  const linkUrl = (provider: ExternalProvider) =>
    provider === "google"
      ? googleStartUrl(API_URL, "/account/identities", true)
      : providerStartUrl(API_URL, provider, "/account/identities", true);
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Sign-in methods</h1>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <ul className="divide-y divide-slate-100 text-sm">
        <li className="flex items-center justify-between py-3">
          <div>
            <div className="font-medium">Email and password</div>
            <div className="text-xs text-slate-500">
              {me.has_password ? "Password set" : "No password"}
            </div>
          </div>
        </li>
        {PROVIDERS.map((provider) => {
          const linked = (identities.data ?? []).find(
            (i) => i.provider === provider,
          );
          const label = PROVIDER_LABELS[provider];
          return (
            <li
              key={provider}
              className="flex items-center justify-between py-3"
            >
              <div>
                <div className="font-medium">{label}</div>
                <div className="text-xs text-slate-500">
                  {linked
                    ? `${linked.email ?? ""} · linked ${formatDateTime(linked.linked_at)}`
                    : "Not linked"}
                </div>
              </div>
              {linked ? (
                <Button
                  variant="secondary"
                  onClick={() => unlink.mutate(provider)}
                  loading={unlink.isPending}
                >
                  Unlink
                </Button>
              ) : (
                <a
                  className="rounded-md border border-slate-300 px-4 py-2 text-sm"
                  href={linkUrl(provider)}
                >
                  Link {label}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
